<template>
  <div class="file-upload position-relative" @paste="handlePaste">
    <div
        v-if="!hasImage"
        @dragover.prevent
        @dragenter.prevent
        @dragstart.prevent
        @drop.prevent="handleFileChange($event.dataTransfer)"
        class="file-upload__area position-relative"
    >
      <svg xmlns="http://www.w3.org/2000/svg"
           class="icon icon-tabler icon-tabler-upload"
           width="44" height="44" viewBox="0 0 24 24"
           stroke-width="1.5" stroke="#2c3e50" fill="none"
           stroke-linecap="round" stroke-linejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"/>
        <path d="M7 9l5 -5l5 5"/>
        <path d="M12 4l0 12"/>
      </svg>
      <div class="position-absolute end-0 top-0 w-100 h-100">
        <input type="file"
               ref="fileInput"
               @change="handleFileChange($event.target)"
        />
        <div v-if="errors.length > 0" class="file-upload__error">
          <div v-for="(error, index) in errors" :key="index">{{ $t(error) }}</div>
        </div>
      </div>
    </div>

    <div ref="cutImage" v-else class="upload-preview d-flex">
      <button type="button" class="btn btn-outline-danger p-2 position-absolute end-0 top-0 p-1"
              @click="resetFileInput($event)">
        X
      </button>
      <img :data-fancybox="'cutImages'" v-if="file.isImage" :src="file.url" class="mw-100 h-100 mh-100 object-fit-cover" alt="Uploaded Image"/>
      <img :data-fancybox="'cutImages'" v-if="hasImage" class="mw-100 h-100 mh-100 object-fit-cover" :src="'/' + file.path" alt="img">
    </div>
  </div>
</template>

<script>
import {Fancybox} from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import {log} from "@/utils/clientLogger.js";

export default {
  name: "ImageUpload",
  props: {
    maxSize: {type: Number, default: 5, required: true},
    accept: {type: String, default: "image/*"},
    imageId: {type: String, default: ""},
  },
  data() {
    return {
      errors: [],
      file: {
        name: "",
        size: 0,
        type: "",
        fileExtention: "",
        url: "",
        isImage: false,
        isUploaded: false,
        patch: ""
      },
    };
  },
  computed: {
    hasImage() {
      return this.isValidObjectId(this.imageId);
    }
  },
  mounted() {
    if (this.hasImage) {
      this.file.isUploaded = true;
      this.$http.get(`/api/files/${this.imageId}`).then(response => {
        if (response.data) {
          this.file = response.data;
          this.$emit("file-uploaded", response.data);
        } else {
          this.file.isUploaded = false;
          this.$emit("file-uploaded", {_id: null});
        }
      }).catch(error => {
        log.error('View:ImageUpload:mounted', {'error': error})
      })
    }

    document.addEventListener("paste", this.handlePaste);
    Fancybox.bind(this.$refs.cutImage, '[data-fancybox]', {});
  },
  updated() {
    Fancybox.unbind(this.$refs.cutImage);
    Fancybox.bind(this.$refs.cutImage, '[data-fancybox]', {});
  },
  beforeUnmount() {
    document.removeEventListener("paste", this.handlePaste);
    Fancybox.unbind(this.$refs.cutImage);
  },
  methods: {
    handlePaste(event) {
      const items = event.clipboardData?.items || [];
      for (const item of items) {
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            this.errors = [];
            if (this.isFileValid(file)) {
              this.loadFile(file);
            } else {
              log.warn("View:ImageUpload:handlePaste", {
                'message': 'The file from the buffer did not pass validation'
              });
            }
          }
        }
      }
    },
    isValidObjectId(id) {
      return typeof id === 'string' && id.length === 24 && /^[a-f\d]{24}$/i.test(id);
    },
    handleFileChange(event) {
      const files = event?.files || event?.target?.files || event?.dataTransfer?.files;
      if (!files || !files.length) return;

      const file = files[0];
      this.errors = [];
      if (this.isFileValid(file)) {
        this.loadFile(file);
      }
    },
    loadFile(file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.file = {
          name: file.name.split(".")[0],
          size: file.size ? Math.round((file.size / 1024 / 1024) * 100) / 100 : 0,
          type: file.type,
          fileExtention: file.name.split(".").pop(),
          isImage: file.type.startsWith("image"),
          url: reader.result,
          isUploaded: true,
        };
      };
      reader.readAsDataURL(file);
      this.uploadFile(file);
    },
    async uploadFile(fileData) {
      const formData = new FormData();
      formData.append('file', fileData); // <-- обов'язково ключ 'file'

      try {
        const response = await this.$http.post('/api/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: this.$store.getters.userToken
          }
        });
        log.info('View:ImageUpload:uploadFile', {
          'response': response.data,
          'message':'Uploaded file'
        });
        this.$emit("file-uploaded", response.data);
      } catch (error) {
        log.error('View:ImageUpload:uploadFile', {'error': error})
      }
    },
    resetFileInput(e) {
      e.preventDefault();
      if (!this.$t || !confirm(this.$t('messages.confirmImageDelete'))) return;

      this.file.isUploaded = true;
      if (this.isValidObjectId(this.imageId)) {
        this.$http.delete(`/api/upload/${this.imageId}`).then(response => {
          this.$emit("file-uploaded", {_id: null, response});
        }).catch(error => {
          log.error('View:ImageUpload:resetFileInput', {'error': error})
        })
      }

      this.$nextTick(() => {
        this.file = {
          name: "",
          size: 0,
          type: "",
          data: "",
          fileExtention: "",
          url: "",
          isImage: false,
          isUploaded: false,
        };
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
        }
      });
    },
    isFileSizeValid(fileSize) {
      if (fileSize <= this.maxSize) return true;
      this.errors.push('messages.fileTooLarge');
      return false;
    },
    isFileTypeValid(fileExtention) {
      if (this.accept.split(",").includes(fileExtention)) return true;
      this.errors.push('messages.invalidFileType');
      return false;
    },
    isFileValid(file) {
      const fileSize = Math.round((file.size / 1024 / 1024) * 100) / 100;
      const fileExtention = file.name.split(".").pop();
      return this.isFileSizeValid(fileSize) && this.isFileTypeValid(fileExtention);
    },
  }
};
</script>

<style lang="scss" scoped>
.file-upload {
  height: 250px;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  &__area {
    width: 100%;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed #ccc;
  }

  &__error {
    margin-top: 10px;
    color: #f00;
    font-size: 12px;
  }

  .upload-preview {
    height: 100%;
    width: 100%;
    justify-content: start;
    flex-direction: column;
  }
}

.upload-preview .file-extension {
  height: 100px;
  width: 100px;
  border-radius: 8px;
  background: #ccc;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5em auto;
  font-size: 1.2em;
  padding: 1em;
  text-transform: uppercase;
  font-weight: 500;
}

.upload-preview .file-name {
  font-size: 1.2em;
  font-weight: 500;
  color: #000;
  opacity: 0.5;
}

input[type="file"] {
  position: absolute;
  opacity: 0;
  width: inherit;
  min-height: 100%;
  max-height: 100%;
  cursor: pointer;
}
</style>